/**
 * Products 2.0 Shopify line-item property serializer.
 *
 * This is the only boundary that may turn calculator state into properties
 * submitted to Shopify. Recipe controllers may assemble temporary/raw values,
 * but this serializer applies the fixed order allowlist and compatibility
 * contract before the cart request is made.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ZugLineItemProperties = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var DISPLAY_ORDER = [
    'Shape',
    'Finished Size',
    'Pieces',
    'Material',
    'Finish',
    'Turnaround',
    'Easy Peel Tab',
    'Application Method',
    'Application Side',
    'Unwind Direction',
    'Artwork Status',
    'Original Artwork',
    'Proof Preview',
    'Artwork Note',
    'Job ID',
    'Sample Product',
    'Sample Offer'
  ];

  /*
   * These fields have verified runtime or workflow dependencies. Pricing and
   * geometry diagnostics deliberately do not appear here; they belong in the
   * compact _calc_data object.
   */
  var COMPATIBILITY_PRIVATE_ORDER = [
    'calc_line_uid',
    '_pricing_engine',
    '_line_type',
    '_upload_id',
    '_artwork_mode',
    '_artwork_approved',
    '_artwork_original_url',
    '_dcf_preview_url',
    '_previous_order_number',
    '_sample_mode',
    '_sample_key',
    '_sample_price_cents',
    '_sticker_sheets',
    '_roll_labels',
    '_label_sheets',
    '_fixed_shape_recipe',
    '_bundle_quantity_locked',
    '_bundle_display_quantity',
    '_lamination',
    '_print_orientation',
    '_white_ink',
    '_white_ink_instruction',
    '_mirror_reverse_instruction',
    '_white_base',
    '_varnish_mask_instruction',
    '_original_filename',
    '_is_coaster',
    '_is_print_fixed',
    '_print_fixed_id',
    '_print_fixed_native_uid',
    '_print_fixed_double_sided',
    '_preview_template',
    '_preview_transform',
    '_placement_state',
    '_coaster_double_sided',
    '_coaster_front_upload_id',
    '_coaster_front_original_url',
    '_coaster_front_preview_url',
    '_coaster_front_rendered_preview_url',
    '_coaster_back_upload_id',
    '_coaster_back_original_url',
    '_coaster_back_preview_url',
    '_coaster_back_rendered_preview_url',
    '_pf_front_upload_id',
    '_pf_front_original_url',
    '_pf_front_preview_url',
    '_pf_back_upload_id',
    '_pf_back_original_url',
    '_pf_back_preview_url'
  ];

  var MAX_STANDARD_TOP_LEVEL_PROPERTIES = 20;

  function firstValue(source, keys) {
    for (var index = 0; index < keys.length; index += 1) {
      var value = source[keys[index]];
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return '';
  }

  function optionalValue(value) {
    if (value === undefined || value === null || value === false) return '';
    var text = String(value).trim();
    return text ? text : '';
  }

  function finiteNumber(value) {
    if (value === '' || value === null || value === undefined) return null;
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function compactNumber(value, decimals) {
    var number = finiteNumber(value);
    if (number === null) return null;
    return Number(number.toFixed(decimals == null ? 6 : decimals));
  }

  function formatDimensionNumber(value) {
    var number = finiteNumber(value);
    return number === null ? '' : number.toFixed(2);
  }

  function formatFinishedSize(width, height, fallback) {
    var formattedWidth = formatDimensionNumber(width);
    var formattedHeight = formatDimensionNumber(height);
    if (formattedWidth && formattedHeight) {
      return formattedWidth + ' × ' + formattedHeight + ' in';
    }

    var text = optionalValue(fallback);
    if (!text) return '';
    return text
      .replace(/\s*[x×]\s*/i, ' × ')
      .replace(/"/g, ' in')
      .replace(/″/g, ' in');
  }

  function shortJobId(value) {
    var text = optionalValue(value);
    if (!text) return '';
    var compact = text.replace(/[^a-z0-9]/gi, '');
    return (compact.slice(0, 8) || text.slice(0, 8)).toUpperCase();
  }

  function artworkStatus(raw) {
    var explicit = optionalValue(raw['Artwork Status']);
    if (explicit) return explicit;

    var mode = optionalValue(raw._artwork_mode).toLowerCase();
    if (mode === 'email_later') return 'Upload Later';
    if (mode === 'reorder') {
      var previousOrder = optionalValue(raw._previous_order_number);
      return previousOrder ? 'Reorder — ' + previousOrder : 'Reorder';
    }
    if (mode === 'upload_artwork') return 'Uploaded';
    if (optionalValue(raw._artwork_approved).toLowerCase() === 'true') return 'Approved';
    if (firstValue(raw, ['_dcf_preview_url', 'Proof Preview'])) return 'Proof Generated';
    return '';
  }

  function differentPair(pair, reference) {
    if (!pair || pair.length !== 2 || pair[0] === null || pair[1] === null) return null;
    if (!reference || reference.length !== 2) return pair;
    return pair[0] !== reference[0] || pair[1] !== reference[1] ? pair : null;
  }

  function buildCalcData(raw, calc) {
    calc = calc || {};
    var width = compactNumber(firstValue(calc, ['width', 'w']) || firstValue(raw, ['_width_in', 'width_in']), 4);
    var height = compactNumber(firstValue(calc, ['height', 'h']) || firstValue(raw, ['_height_in', 'height_in']), 4);
    var basePair = [width, height];
    var physicalPair = [
      compactNumber(firstValue(calc, ['physicalWidth']) || raw._physical_width_in, 4),
      compactNumber(firstValue(calc, ['physicalHeight']) || raw._physical_height_in, 4)
    ];
    var bboxSource = calc.bbox || [
      raw._bbox_width_px,
      raw._bbox_height_px,
      raw._bbox_x_px,
      raw._bbox_y_px
    ];
    var bbox = bboxSource.map(function (value) { return compactNumber(value, 2); });

    var data = {};
    var version = optionalValue(firstValue(calc, ['version', 'v']) || raw._calc_version);
    var area = compactNumber(firstValue(calc, ['area']) || firstValue(raw, ['_area_in2', 'area_sq_in']), 4);
    var tier = compactNumber(firstValue(calc, ['tier']) || raw._size_tier_in2, 4);
    var quantity = compactNumber(firstValue(calc, ['quantity', 'qty']) || firstValue(raw, ['_qty_entered', 'Pieces']), 0);
    var unit = compactNumber(firstValue(calc, ['unit']) || firstValue(raw, ['_u_final', 'unit_price']), 6);
    var totalCents = compactNumber(firstValue(calc, ['totalCents']), 0);
    var material = optionalValue(firstValue(calc, ['material']) || raw._material_key);
    var rush = optionalValue(firstValue(calc, ['rush']) || raw._rush_option);
    var finishKey = optionalValue(firstValue(calc, ['finishKey', 'fk']));
    var application = optionalValue(firstValue(calc, ['application', 'app']) || raw._application_method);
    var unwind = optionalValue(firstValue(calc, ['unwind', 'uw']) || raw._unwind_direction);
    var corner = compactNumber(firstValue(calc, ['corner']) || raw._dcf_corner_radius_in, 4);

    if (version) data.v = version;
    if (width !== null) data.w = width;
    if (height !== null) data.h = height;
    if (area !== null) data.area = area;
    if (tier !== null) data.tier = tier;
    if (quantity !== null) data.qty = quantity;
    if (unit !== null) data.unit = unit;
    if (totalCents !== null) data.total = totalCents;
    if (material) data.material = material;
    if (rush) data.rush = rush;
    if (finishKey) data.fk = finishKey;
    if (application) data.app = application;
    if (unwind) data.uw = unwind;
    if (corner !== null) data.corner = corner;
    if (bbox.length === 4 && bbox.every(function (value) { return value !== null; })) data.bbox = bbox;

    var physical = differentPair(physicalPair, basePair);
    if (physical) data.physical = physical;

    return data;
  }

  function serialize(options) {
    options = options || {};
    var raw = options.raw || {};
    var calc = options.calc || {};
    var output = {};
    var display = {
      'Shape': firstValue(raw, ['Shape', 'shape']),
      'Finished Size': formatFinishedSize(
        firstValue(calc, ['width', 'w']) || firstValue(raw, ['_width_in', 'width_in']),
        firstValue(calc, ['height', 'h']) || firstValue(raw, ['_height_in', 'height_in']),
        firstValue(raw, ['Finished Size', 'Size', 'size_label'])
      ),
      'Pieces': firstValue(raw, ['Pieces', '_qty_entered']),
      'Material': firstValue(raw, ['Material', 'material']),
      'Finish': raw.Finish,
      'Turnaround': firstValue(raw, ['Turnaround', 'Rush']),
      'Easy Peel Tab': raw['Easy Peel Tab'],
      'Application Method': firstValue(raw, ['Application Method', 'Apply Location']),
      'Application Side': raw['Application Side'],
      'Unwind Direction': raw['Unwind Direction'],
      'Artwork Status': artworkStatus(raw),
      'Original Artwork': firstValue(raw, ['Original Artwork', '_artwork_original_url']),
      'Proof Preview': firstValue(raw, ['Proof Preview', '_dcf_preview_url']),
      'Artwork Note': firstValue(raw, ['Artwork Note', '_artwork_note']),
      'Job ID': shortJobId(firstValue(raw, ['calc_line_uid', 'Job ID'])),
      'Sample Product': raw['Sample Product'],
      'Sample Offer': raw['Sample Offer']
    };

    DISPLAY_ORDER.forEach(function (key) {
      var value = optionalValue(display[key]);
      if (value) output[key] = value;
    });

    COMPATIBILITY_PRIVATE_ORDER.forEach(function (key) {
      var value = optionalValue(raw[key]);
      if (value) output[key] = value;
    });

    var calcData = buildCalcData(raw, calc);
    if (Object.keys(calcData).length) output._calc_data = JSON.stringify(calcData);

    return output;
  }

  function serializeProducts2(options) {
    options = options || {};
    var pricing = options.pricing || {};
    var upload = options.upload || {};
    var raw = Object.assign({}, options.raw || {});
    if (String(options.material || '') === 'white_static_cling') {
      raw._white_ink = 'true';
      raw._white_ink_instruction = 'White ink included';
    }
    if (String(options.material || '') === 'clear_static_cling') {
      raw._white_ink = 'true';
      raw._white_ink_instruction = 'White ink included';
      if (raw._print_orientation === 'mirrored') {
        raw._mirror_reverse_instruction =
          'Mirror/reverse artwork during production setup';
      } else {
        delete raw._mirror_reverse_instruction;
      }
    }
    if (String(options.material || '') === 'spot_uv') {
      raw._varnish_mask_instruction = 'ZUG creates varnish mask';
      if (upload.original_filename) {
        raw._original_filename = String(upload.original_filename);
      }
    }
    return serialize({
      raw: raw,
      calc: {
        version: options.version || pricing.version || 'v2',
        width: firstValue(pricing, ['width_in', 'width']),
        height: firstValue(pricing, ['height_in', 'height']),
        area: firstValue(pricing, ['area_in2', 'areaSqIn', 'area']),
        tier: pricing.size_tier_in2,
        quantity: options.quantity,
        unit: firstValue(pricing, ['u_final', 'unitPrice', 'unit']),
        totalCents: options.totalCents,
        material: options.material,
        rush: options.rush || pricing.rush_option,
        finishKey: pricing.finish_key || '',
        application: options.application || '',
        unwind: options.unwind || '',
        physicalWidth: upload.physical_width_in,
        physicalHeight: upload.physical_height_in,
        corner: upload.corner_radius_in,
        bbox: [
          upload.bbox_width_px,
          upload.bbox_height_px,
          upload.bbox_x_px,
          upload.bbox_y_px
        ]
      }
    });
  }

  function parseCalcData(properties) {
    if (!properties || !properties._calc_data) return null;
    try {
      var parsed = JSON.parse(properties._calc_data);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  return {
    DISPLAY_ORDER: DISPLAY_ORDER,
    COMPATIBILITY_PRIVATE_ORDER: COMPATIBILITY_PRIVATE_ORDER,
    MAX_STANDARD_TOP_LEVEL_PROPERTIES: MAX_STANDARD_TOP_LEVEL_PROPERTIES,
    buildCalcData: buildCalcData,
    formatFinishedSize: formatFinishedSize,
    parseCalcData: parseCalcData,
    serialize: serialize,
    serializeProducts2: serializeProducts2,
    shortJobId: shortJobId
  };
});
